import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import CookieConsentBanner from "@/components/CookieConsent/CookieConsentBanner";
import CookiePreferenceCenter from "@/components/CookieConsent/CookiePreferenceCenter";
import ThirdPartyScripts from "@/components/CookieConsent/ThirdPartyScripts";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/Home"; // eager — landing page

// Route-level code-splitting for everything else.
// Each chunk is fetched on demand, keeping the initial bundle small.
const About            = lazy(() => import("@/pages/About"));
const Disclosures      = lazy(() => import("@/pages/Disclosures"));
const FinancialReports = lazy(() => import("@/pages/FinancialReports"));
const News             = lazy(() => import("@/pages/News"));
const NewsDetail       = lazy(() => import("@/pages/NewsDetail"));
const Contact          = lazy(() => import("@/pages/Contact"));
const CMSPage          = lazy(() => import("@/pages/CMSPage"));

// Admin bundle — never loaded for public visitors.
const AdminLogin       = lazy(() => import("@/pages/admin/Login"));
const AdminDashboard   = lazy(() => import("@/pages/admin/Dashboard"));
const ProtectedAdmin   = lazy(() => import("@/pages/admin/ProtectedAdmin"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-foreground/40 text-xs font-mono uppercase tracking-widest">
      Loading…
    </div>
  );
}

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
         <CookieConsentProvider>
          <BrowserRouter>
            <Toaster position="top-right" />
            <ThirdPartyScripts />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  {/* Legacy /about → new canonical URL. The About template is
                      now the content of "The Asiabest Difference". */}
                  <Route path="/about" element={<Navigate to="/p/the-asiabest-difference" replace />} />
                  <Route path="/disclosures" element={<Disclosures />} />
                  <Route path="/financial-reports" element={<FinancialReports />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/news/:slug" element={<NewsDetail />} />
                  <Route path="/contact" element={<Contact />} />
                  {/* Route override: The Asiabest Difference renders the rich
                      About-template component instead of the generic CMSPage
                      renderer. Data still comes from /api/about. Must be listed
                      before the /p/:slug catch-all. */}
                  <Route path="/p/the-asiabest-difference" element={<About />} />
                  <Route path="/p/:slug" element={<CMSPage />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdmin>
                      <AdminDashboard />
                    </ProtectedAdmin>
                  }
                />
              </Routes>
            </Suspense>
            <CookieConsentBanner />
            <CookiePreferenceCenter />
          </BrowserRouter>
        </CookieConsentProvider>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
